import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaTrashAlt, FaEdit } from 'react-icons/fa';
import api from '../../services/api';

/* Material UI */
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { Card, CardContent, Grid, Typography } from '@mui/material';

import './styles.css';
import Table from '../../components/Table';

/* alerta */
import Notification from '../../components/Notification/Notification';
import ConfirmDialog from '../../components/Notification/ConfirmDialog';
import UserPermission from '../../components/UserPermission';

export default function MaterialRegister() {
  const history = useNavigate();

  /* Permissão do usuário no sistema */
  const userPermission = UserPermission();

  const [name, setName] = useState('');
  const [unit, setUnit] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [refresh, setRefresh] = useState(true);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '', title: '' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', subTitle: '' });

  const isEdit = Boolean(editingId);

  const classes = {
    textTitle: {
      margin: 8,
      color: '#103168',
      fontWeight: 600,
      marginBottom: 30,
    },
    buttonConfirm: {
      backgroundColor: '#1551B3',
      color: '#fff',
      borderRadius: 20,
      height: '45px',
      textTransform: 'capitalize',
      fontWeight: 'bold',
      fontSize: 15,
      marginTop: '17px',
      minWidth: '220px',
    },
    message: {
      padding: 10,
      backgroundColor: '#FFFBE8',
      color: 'rgba(0,0,0,0.8)',
      borderRadius: 7,
      width: '100%',
    },
    cardRoot: {
      borderRadius: '17px',
      backgroundColor: '#F5F5F5',
      boxShadow: 'none',
    },
    labelInput: {
      fontFamily: 'Rawline',
      color: '#333333',
      fontWeight: 'bold',
      paddingLeft: 2,
      fontSize: 14,
      marginBottom: '0.5rem',
    },
  };

  const cleanFields = () => {
    setName('');
    setUnit('');
    setEditingId(null);
  };

  /* Função para deletar um material */
  const handleDeleteMaterial = useCallback(async (id) => {
    try {
      await api.delete(`/materials/${id}`);
      setNotify({
        isOpen: true,
        message: 'Material deletado com sucesso.',
        type: 'error',
        title: 'Deletado',
      });
      setRefresh(true);
    } catch (err) {
      setNotify({
        isOpen: true,
        message: 'Ocorreu um erro ao deletar o material.',
        type: 'error',
        title: 'Erro',
      });
    } finally {
      setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
    }
  }, []);

  const deleteMaterialDialog = useCallback((id) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Você gostaria de deletar esse material?',
      subTitle: 'Esta ação não poderá ser desfeita',
      onConfirm: () => { handleDeleteMaterial(id); },
    });
  }, [handleDeleteMaterial]);

  const startEdit = useCallback((material) => {
    setEditingId(material.id);
    setName(material.name ?? '');
    setUnit(material.unit ?? '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  /* Colunas da tabela */
  const columns = useMemo(
    () => [
      {
        Header: 'Nome',
        accessor: 'name',
      },
      {
        Header: 'Unidade',
        accessor: 'unit',
      },
      {
        Header: () => null,
        id: 'edit',
        Cell: ({ row }) => (
          <Link to="#" onClick={() => startEdit(row.original)}>
            <FaEdit size={20} color="#1351B4" />
          </Link>
        ),
      },
      {
        Header: () => null,
        id: 'delete',
        Cell: ({ row }) => (
          <Link to="#">
            <FaTrashAlt
              size={20}
              color="#1351B4"
              onClick={() => { deleteMaterialDialog(row.original.id); }}
            />
          </Link>
        ),
      },
    ],
    [deleteMaterialDialog, startEdit]
  );

  useEffect(() => {
    if (userPermission === 'admin' && refresh) {
      api.get('materials').then((response) => {
        setMaterials(response.data);
      });
      setRefresh(false);
    }
  }, [userPermission, refresh]);

  /* Chama o alerta para cadastrar/atualizar */
  function confirms(e) {
    e && e.preventDefault();

    if (!name || !unit) {
      setNotify({
        isOpen: true,
        message: 'Preencha os campos Nome e Unidade.',
        type: 'warning',
      });
      return;
    }

    /* Verifica duplicidade (ignora o próprio registro em edição) */
    const duplicated = materials.some(
      (m) => m.name.toLowerCase() === name.toLowerCase() && m.id !== editingId
    );
    if (duplicated) {
      setNotify({
        isOpen: true,
        message: 'Já existe um material com esse nome.',
        type: 'warning',
      });
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: isEdit ? `Você está atualizando o material "${name}"?` : `Você está cadastrando o material "${name}"?`,
      subTitle: 'Confirme as informações do material.',
      onConfirm: () => { handleSaveMaterial(); },
    });
  }

  async function handleSaveMaterial() {
    const data = { name, unit };

    try {
      if (isEdit) {
        await api.patch(`/materials/${editingId}`, data);
      } else {
        await api.post('materials', data);
      }

      cleanFields();
      setRefresh(true);
      setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
      setNotify({
        isOpen: true,
        message: isEdit ? 'Material atualizado com sucesso.' : 'Material cadastrado com sucesso.',
        type: 'success',
        title: isEdit ? 'Atualizado' : 'Cadastrado',
      });
    } catch (err) {
      setNotify({
        isOpen: true,
        message: isEdit ? 'Ocorreu um erro ao atualizar o material.' : 'Ocorreu um erro ao cadastrar o material.',
        type: 'error',
        title: 'Erro',
      });
      setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
    }
  }

  if (userPermission !== 'admin') {
    return (
      <>
        {alert('Seu usuário não possuí privilégios para acessar essa página!')}
        {history('/search')}
      </>
    );
  }

  return (
    <div className="cadastro-container">
      <form onSubmit={confirms}>
        <Card style={classes.cardRoot}>
          <CardContent>

            <Typography variant="h5" align="center" style={classes.textTitle}>
              {isEdit ? 'Editar Material / Insumo' : 'Cadastro de Materiais / Insumos'}
            </Typography>

            <div style={classes.message}>
              Preencha o formulário abaixo para cadastrar um material ou insumo que poderá ser
              utilizado nas ocorrências internas.
            </div>

            <Grid id="form-inputs" container spacing={2} alignItems="center" style={{ marginTop: 20 }}>
              <Grid item xs={12} sm={8}>
                <Typography style={classes.labelInput}>
                  Nome do Material / Insumo: *
                </Typography>

                <TextField
                  style={{ background: '#fff' }}
                  required
                  fullWidth
                  value={name}
                  onChange={e => setName(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography style={classes.labelInput}>
                  Unidade (ex.: t, L, un, m, sc): *
                </Typography>

                <TextField
                  style={{ background: '#fff' }}
                  required
                  fullWidth
                  value={unit}
                  onChange={e => setUnit(e.target.value)}
                />
              </Grid>
            </Grid>

          </CardContent>
        </Card>

        <Grid container spacing={2} alignItems="center" justifyContent="center">
          <Grid item>
            <Button
              id="register-button"
              variant="outlined"
              type="submit"
              startIcon={<FaEdit style={{ color: '#fff' }} />}
              style={classes.buttonConfirm}
            >
              {isEdit ? 'Atualizar Material' : 'Cadastrar Material'}
            </Button>
          </Grid>
          {isEdit && (
            <Grid item>
              <Button
                variant="outlined"
                onClick={cleanFields}
                style={{ ...classes.buttonConfirm, backgroundColor: '#9e9e9e' }}
              >
                Cancelar
              </Button>
            </Grid>
          )}
        </Grid>
      </form>

      <hr />

      <Card id="materials-list" style={classes.cardRoot}>
        <CardContent>
          <div>
            <Typography variant="h5" align="center" style={classes.textTitle}>
              Listagem de Materiais / Insumos
            </Typography>

            <div style={classes.message}>
              Abaixo serão listados os materiais cadastrados no sistema.
              É possível editar ou excluir um material selecionando uma das opções à direita da tabela.
            </div>
            <Table
              searchBy="name"
              label="Pesquise um material"
              columns={columns}
              data={materials}
            />
          </div>
        </CardContent>
      </Card>

      <Notification notify={notify} setNotify={setNotify} />
      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
    </div>
  );
}
